import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useRef } from 'react'
import { AppDispatch } from '@store/store'
import {
  selectRunningTaskIds,
  selectUnresolvedTaskIds,
} from '@store/selectors/task.selectors'
import { updateRunningTasks } from '@store/thunk/task.thunk'
import { EntityId } from '@reduxjs/toolkit'
import { selectAllTasks, TaskModel } from '@store/slices/task.slice'
import { resolveTask } from '@store/actions/task.actions'
import {
  fetchAllPlaylists,
  PlaylistModel,
  setPlaylistProgressFinished,
} from '@store/slices/playlist.slice'
import { toast } from 'react-toastify'
import { DjangoFilter } from '@store/slices/generic.slice'
import {
  AISlotModel,
  fetchAllAiSlots,
  setAiSlotProgressFinished,
} from '@store/slices/aiSlot.slice'
import {
  AIMetadataModel,
  fetchAllMetadata,
  setAiMetadataFinished,
} from '@store/slices/aiMeta.slice'
import {
  fetchAllNarrations,
  setNarrationProgressFinished,
} from '@store/slices/narration.slice'

const useTaskPolling = (delay: number) => {
  const dispatch = useDispatch<AppDispatch>()
  const allTasks = useSelector(selectAllTasks)
  const runningTasks = useSelector(selectRunningTaskIds)
  const unresolvedTaskIds = useSelector(selectUnresolvedTaskIds)

  const savedCallback = useRef<() => void>(() => null)

  const resolveTaskCallback = (taskId: EntityId) => {
    const task: TaskModel | undefined = allTasks.find(
      (task) => task.id == taskId
    )
    if (task && task.relatedObjects) {
      if (task.success && (!task.result || task.result.success)) {
        toast.success(`Úkol '${task.title}' úspěšně doběhl`)
      } else {
        toast.error(
          `Úkol '${task.title}' dopadl neúsupěchem.\n${
            task.result && task.result.message ? ` ${task.result?.message}` : ''
          }`
        )
      }
      updateRelatedObject(task.relatedObjects)
      dispatch(resolveTask({ id: task.id }))
    }
  }

  const updateRelatedObject = (
    relatedObjects: {
      entityName: string
      entityIds: EntityId[]
    }[]
  ) => {
    relatedObjects.forEach((relatedObject) => {
      if (relatedObject.entityIds.length > 0)
        switch (relatedObject.entityName) {
          case 'playlist':
            const playlistFilter = new DjangoFilter<PlaylistModel>()
            playlistFilter.addIdFilter(relatedObject.entityIds)
            dispatch(fetchAllPlaylists(playlistFilter))
            dispatch(setPlaylistProgressFinished(relatedObject.entityIds))
            return
          case 'aiSlot':
            const slotFilter = new DjangoFilter<AISlotModel>()
            slotFilter.addIdFilter(relatedObject.entityIds)
            dispatch(fetchAllAiSlots(slotFilter))
            dispatch(setAiSlotProgressFinished(relatedObject.entityIds))
            return
          case 'aiMetadata':
            const aiMetadataFilter = new DjangoFilter<AIMetadataModel>()
            aiMetadataFilter.addIdFilter(relatedObject.entityIds)
            dispatch(fetchAllMetadata(aiMetadataFilter))
            dispatch(setAiMetadataFinished(relatedObject.entityIds))
            return
          case 'narration':
            const narrationFilter = new DjangoFilter<AIMetadataModel>()
            narrationFilter.addIdFilter(relatedObject.entityIds)
            dispatch(fetchAllNarrations(narrationFilter))
            dispatch(setNarrationProgressFinished(relatedObject.entityIds))
            return
          default:
            toast.error('Unknown task object to resolve')
            console.error(
              `Unable to resolve entity name: ${relatedObject.entityName}`
            )
        }
    })
  }

  const updateCompleted = () => {
    const unresolvedTasks = allTasks.filter((task) =>
      unresolvedTaskIds.includes(task.id)
    )
    if (unresolvedTaskIds.length > 0) {
      unresolvedTasks.forEach((task) => resolveTaskCallback(task.id))
    }
  }

  useEffect(() => {
    savedCallback.current = () => dispatch(updateRunningTasks(runningTasks))
  }, [dispatch, runningTasks])

  useEffect(() => {
    updateCompleted()
    if (runningTasks.length === 0) return

    const tick = () => {
      if (savedCallback.current) {
        savedCallback.current()
      }
    }

    const id = setInterval(tick, delay)
    return () => clearInterval(id)
  }, [runningTasks.length, delay])
}

export default useTaskPolling
